import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { Search, Download, BookUser, Mail, Phone, Globe, Building2 } from "lucide-react";

export const Route = createFileRoute("/adressregister")({
  head: () => ({
    meta: [
      { title: "Adressregister — Inkubera" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AppShell>
      <Inner />
    </AppShell>
  ),
});

type Contact = {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  type: "Bolag" | "Prospect" | "Coach";
  category: string; // fas / status / aktivitet
  active: boolean;
  hasEngagement?: boolean;
};

const CATEGORY_OPTIONS = [
  "Prospect",
  "Förinkubation",
  "Inkubation",
  "Acceleration",
  "Aktivt",
  "Passivt",
  "Coach – aktiv",
  "Coach – passiv",
];

function Inner() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [types, setTypes] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [coachEngagement, setCoachEngagement] = useState<"alla" | "med" | "utan">("alla");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ data: comps }, { data: pros }, { data: chs }] = await Promise.all([
        supabase.from("companies").select("id, name, contact_name, contact_email, municipality, fas, status").order("name"),
        supabase.from("prospects").select("id, case_name, contact_name, contact_email, municipality, status").order("case_name"),
        supabase.from("coaches").select("id, full_name, email, phone, city, linkedin_url, active, engagements_count").order("full_name"),
      ]);

      const all: Contact[] = [];

      (comps ?? []).forEach((c: any) => {
        all.push({
          id: `c-${c.id}`,
          name: c.contact_name ?? "—",
          company: c.name,
          email: c.contact_email ?? "",
          phone: "",
          website: "",
          type: "Bolag",
          category: c.status === "Passivt" ? "Passivt" : (c.fas ?? "Aktivt"),
          active: c.status !== "Passivt",
        });
      });

      (pros ?? []).forEach((p: any) => {
        all.push({
          id: `p-${p.id}`,
          name: p.contact_name ?? "—",
          company: p.case_name,
          email: p.contact_email ?? "",
          phone: "",
          website: "",
          type: "Prospect",
          category: "Prospect",
          active: true,
        });
      });

      (chs ?? []).forEach((c: any) => {
        all.push({
          id: `co-${c.id}`,
          name: c.full_name,
          company: "—",
          email: c.email,
          phone: c.phone ?? "",
          website: c.linkedin_url ?? "",
          type: "Coach",
          category: c.active ? "Coach – aktiv" : "Coach – passiv",
          active: !!c.active,
          hasEngagement: (c.engagements_count ?? 0) > 0,
        });
      });

      setContacts(all);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return contacts.filter((c) => {
      if (types.length && !types.includes(c.type)) return false;
      if (categories.length && !categories.includes(c.category)) return false;
      if (c.type === "Coach" && coachEngagement !== "alla") {
        if (coachEngagement === "med" && !c.hasEngagement) return false;
        if (coachEngagement === "utan" && c.hasEngagement) return false;
      }
      if (term && !`${c.name} ${c.company} ${c.email} ${c.phone} ${c.website}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [contacts, q, types, categories, coachEngagement]);

  const exportCsv = () => {
    const head = ["Typ", "Kategori", "Namn", "Bolag", "E-post", "Telefon", "Webb"];
    const rows = filtered.map((c) => [c.type, c.category, c.name, c.company, c.email, c.phone, c.website]);
    const csv = [head, ...rows]
      .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `adressregister-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggle = (arr: string[], v: string, set: (a: string[]) => void) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Laddar adresser…</div>;

  return (
    <div className="p-8 max-w-7xl space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
            <BookUser className="size-3.5" /> Kontakter
          </div>
          <h1 className="font-display text-3xl">Adressregister</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Samlade kontaktuppgifter för bolag, prospekt och coacher. Filtrera och
            exportera till CSV.
          </p>
        </div>
        <button
          onClick={exportCsv}
          className="h-10 px-4 rounded-lg bg-foreground text-background text-sm font-medium inline-flex items-center gap-2 hover:opacity-90"
        >
          <Download className="size-4" /> Exportera CSV
        </button>
      </header>

      <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Sök namn, bolag, e-post, telefon…"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
          />
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Typ</div>
          <div className="flex flex-wrap gap-1.5">
            {(["Bolag", "Prospect", "Coach"] as const).map((t) => {
              const active = types.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => toggle(types, t, setTypes)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    active ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Kategori / fas / status</div>
          <div className="flex flex-wrap gap-1.5">
            {CATEGORY_OPTIONS.map((c) => {
              const active = categories.includes(c);
              return (
                <button
                  key={c}
                  onClick={() => toggle(categories, c, setCategories)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    active ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Coach-engagemang</div>
          <div className="flex gap-1.5">
            {([
              ["alla", "Alla"],
              ["med", "Med engagemang"],
              ["utan", "Utan engagemang"],
            ] as const).map(([v, l]) => (
              <button
                key={v}
                onClick={() => setCoachEngagement(v)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  coachEngagement === v ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        {filtered.length} av {contacts.length} kontakter
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Typ</th>
                <th className="text-left px-4 py-3">Kategori</th>
                <th className="text-left px-4 py-3">Namn</th>
                <th className="text-left px-4 py-3">Bolag</th>
                <th className="text-left px-4 py-3">E-post</th>
                <th className="text-left px-4 py-3">Telefon</th>
                <th className="text-left px-4 py-3">Webb</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-secondary/30">
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary">
                      {c.type === "Bolag" && <Building2 className="size-3" />}
                      {c.type}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{c.category}</td>
                  <td className="px-4 py-2.5 font-medium">{c.name || "—"}</td>
                  <td className="px-4 py-2.5">{c.company || "—"}</td>
                  <td className="px-4 py-2.5">
                    {c.email ? (
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-foreground hover:underline">
                        <Mail className="size-3" /> {c.email}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    {c.phone ? (
                      <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:underline">
                        <Phone className="size-3" /> {c.phone}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    {c.website ? (
                      <a href={c.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline truncate max-w-[200px]">
                        <Globe className="size-3" /> {c.website.replace(/^https?:\/\//, "")}
                      </a>
                    ) : "—"}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    Inga kontakter matchar filtret.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Telefon och webb finns idag bara för coacher. För bolag och prospekt visas
        de fält som registrerats. Lägg till telefon/webb i bolagskortet för att
        komplettera registret.
      </p>
    </div>
  );
}
